<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'ViewSpecialApprovals' }" class="mb-0 main-identifier fw-semibold text-decoration-none">
        Special Approvals
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">Update Special Approval</p>
    </div>

    <div class="d-flex justify-content-center mb-5">
      <div class="form-outer">
        <form @submit.prevent="step === totalSteps ? handlePrimarySubmit() : handleNext()">
          <div class="crud-form position-relative">
            <div class="form-inner mb-2">
              <div class="form-header d-flex justify-content-between align-items-center">
                <h1 class="mb-0">Update Special Approvals</h1>
              </div>

              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading special approvals...</p>
              </div>

              <template v-else>
                <div class="header-box d-flex justify-content-between align-items-center mb-3">
                  <div class="progress-bar" :style="{ '--progress-width': progressWidth, '--step-count': totalSteps }">
                    <div class="step-icon" v-for="(s, i) in totalSteps" :key="`icon-${i}`">
                      <div :class="['step', { active: step === i + 1, completed: step > i + 1 }]">
                        {{ step > i + 1 ? '✓' : i + 1 }}
                      </div>
                    </div>
                    <div class="progress-line" :style="{ '--progress-width': progressWidth }" />
                    <div class="step-label text-wrap" v-for="(s, i) in steps" :key="`label-${i}`" :class="{ active: step === i + 1 }">
                      {{ s.label }}
                    </div>
                  </div>
                </div>

                <div v-if="step === 1" class="form-body">
                  <div class="row mb-3">
                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label class="form-label">Originating M.D.A.</label></div>
                      <div class="col-md-9 input-right"><input type="text" class="form-control" v-model="form.originatingMda" readonly /></div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label class="form-label">Org Code</label></div>
                      <div class="col-md-9 input-right"><input type="text" class="form-control" v-model="form.orgCode" readonly /></div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label class="form-label">File Number</label></div>
                      <div class="col-md-9 input-right"><input type="text" class="form-control" v-model="form.fileNumber" readonly /></div>
                    </div>



                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label class="form-label">Budget Year</label></div>
                      <div class="col-md-9 input-right"><input type="number" min="2000" class="form-control" v-model.number="form.budgetYear" readonly /></div>
                    </div>

                    <!-- <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label class="form-label">Quarter</label></div>
                      <div class="col-md-9 input-right">
                        <input type="text" class="form-control" :value="`Q${form.quarter || '-'}`" readonly />
                      </div>
                    </div> -->

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label class="form-label">Nature of Expenditure</label></div>
                      <div class="col-md-9 input-right">
                        <input type="text" class="form-control" :value="natureLabel" readonly />
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label class="form-label">Amount Required (₦)</label></div>
                      <div class="col-md-9 input-right"><input type="text" class="form-control" :value="formatCurrency(totalAmountRequired)" readonly /></div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label class="form-label">Amount Approved (₦)</label></div>
                      <div class="col-md-9 input-right"><input type="text" class="form-control" :value="formatCurrency(totalAmountApproved)" readonly /></div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label class="form-label">Purpose of Request</label></div>
                      <div class="col-md-9 input-right"><textarea class="form-control" rows="3" v-model="form.purposeOfRequest" readonly></textarea></div>
                    </div>

                  </div>
                </div>

                <div v-if="step === 2">
                  <div class="row mb-3 budget-item-wrapper step-2 justify-content-center">
                    <div class="row content-wrapper section-border h-100 w-100">
                      <div class="col d-flex">
                        <div class="d-flex flex-column justify-content-center h-100 w-100">
                          <label class="form-label fw-bold fixed-label">Organizational Code</label>
                          <input type="text" class="form-control view-only-input text-center" v-model="form.orgCode" readonly>
                        </div>
                      </div>
                      <div class="col d-flex">
                        <div class="d-flex flex-column justify-content-center h-100 w-100">
                          <label class="form-label fw-bold fixed-label">Approved Budget Amount (₦)</label>
                          <input type="text" class="form-control view-only-input text-center" :value="Number(form.approvedEstimates || 0).toLocaleString()" readonly>
                        </div>
                      </div>
                      <div class="col d-flex">
                        <div class="d-flex flex-column justify-content-center h-100 w-100">
                          <label class="form-label fw-bold fixed-label">Approved Released To Date (₦)</label>
                          <input type="text" class="form-control view-only-input text-center" :value="Number(form.actualExpenditureYTD || 0).toLocaleString()" readonly>
                        </div>
                      </div>
                      <div class="col d-flex no-border">
                        <div class="d-flex flex-column justify-content-center h-100 w-100">
                          <label class="form-label fw-bold fixed-label">Balance (₦)</label>
                          <input type="text" class="form-control view-only-input text-center" :value="Number(form.balance || 0).toLocaleString()" readonly>
                        </div>
                      </div>
                      <div class="col d-flex no-border">
                        <div class="d-flex flex-column justify-content-center h-100 w-100">
                          <label class="form-label fw-bold fixed-label">Performance (%)</label>
                          <input type="text" class="form-control view-only-input text-center" :value="Number(form.performance || 0).toLocaleString()" readonly>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="table-responsive mt-4" v-if="form.lineItems.length">
                    <table class="table table-bordered text-center align-middle">
                      <thead class="table-light text-center align-middle">
                        <tr>
                          <th class="text-center">Eco Code</th>
                          <th class="text-center">Title</th>
                          <th class="text-center">Approved Budget (₦)</th>
                          <th class="text-center">Released To Date (₦)</th>
                          <th class="text-center">Balance (₦)</th>
                          <th class="text-center">Amount Required (₦)</th>
                          <th class="text-center">Amount Recommended (₦)</th>
                          <th class="text-center">Amount Approved (₦)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, index) in form.lineItems" :key="item.id || index" :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                          <td>{{ item.ecoCode || item.economicCode || '-' }}</td>
                          <td>{{ item.title || '-' }}</td>
                          <td>{{ Number(item.amountBudgeted || 0).toLocaleString() }}</td>
                          <td>{{ Number(item.amountReleased || 0).toLocaleString() }}</td>
                          <td>{{ Number(item.balance || 0).toLocaleString() }}</td>
                          <td>
                            <input
                              v-if="canEditReturned"
                              type="text"
                              class="form-control form-control-sm text-end"
                              :value="formatAmount(item.amountRequired || item.amtRequired || 0)"
                              inputmode="decimal"
                              @input="(event) => onItemAmountInput(event, item)"
                            />
                            <span v-else>{{ formatAmount(item.amountRequired || item.amtRequired || 0) }}</span>
                          </td>
                          <td>
                            <input
                              v-if="canEditReturned"
                              type="number"
                              min="0"
                              step="0.01"
                              class="form-control form-control-sm text-end"
                              v-model.number="item.amountRecommended"
                            />
                            <span v-else>{{ Number(item.amountRecommended || item.eliAmountRecommended || item.amountApproved || 0).toLocaleString() }}</span>
                          </td>
                          <td>
                            <input
                              v-if="canEditReturned"
                              type="text"
                              class="form-control form-control-sm text-end"
                              :value="formatAmount(item.amountApproved || item.amountRecommended || item.eliAmountRecommended || 0)"
                              inputmode="decimal"
                              @input="(event) => onItemApprovedInput(event, item)"
                            />
                            <span v-else>{{ formatAmount(item.amountApproved || item.amountRecommended || item.eliAmountRecommended || 0) }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div v-else class="text-muted fst-italic">No economic line items available.</div>

                </div>

                <div v-if="step === 3" class="form-body">
                  <div class="row mb-3">
                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label class="form-label">Current Status</label></div>
                      <div class="col-md-9 input-right"><input type="text" class="form-control" :value="statusLabel" readonly /></div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label class="form-label">Total Amount Recommended (₦)</label></div>
                      <div class="col-md-9 input-right"><input type="text" class="form-control" :value="formatCurrency(totalAmountRecommended)" readonly /></div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label class="form-label">Total Amount Approved (₦)</label></div>
                      <div class="col-md-9 input-right"><input type="text" class="form-control" :value="formatCurrency(totalAmountApproved)" readonly /></div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label class="form-label">File Approved Date</label></div>
                      <div class="col-md-9 input-right"><input type="date" class="form-control" v-model="form.fileApprovedDate" readonly/></div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label class="form-label">Decision</label></div>
                      <div class="col-md-9 input-right">
                        <input type="text" class="form-control" :value="form.approved ? 'Approved' : form.returned ? 'Returned' : 'Pending'" readonly />
                      </div>
                    </div>

                    <div v-if="form.returnReason" class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label class="form-label">Return Reason</label></div>
                      <div class="col-md-9 input-right"><textarea class="form-control" rows="3" :value="form.returnReason" readonly></textarea></div>
                    </div>

                    <div v-if="warrantData" class="col-md-12 mt-3">
                      <div class="warrant-details-card">
                        <div class="warrant-details-header d-flex justify-content-between align-items-center mb-3">
                          <h5 class="mb-0 fw-semibold">Generated Warrant</h5>
                          <button
                            type="button"
                            class="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                            @click="downloadWarrant(warrantData.id)"
                            :disabled="loadingDownloadWarrant"
                          >
                            <span v-if="loadingDownloadWarrant" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            {{ loadingDownloadWarrant ? 'Downloading...' : 'Download Warrant PDF' }}
                          </button>
                        </div>
                        <div class="row g-3">
                          <div class="col-sm-6">
                            <div class="warrant-field">
                              <span class="warrant-label">Warrant Number</span>
                              <span class="warrant-value">{{ warrantData.warrantNumber || '-' }}</span>
                            </div>
                          </div>
                          <div class="col-sm-6">
                            <div class="warrant-field">
                              <span class="warrant-label">Issue Date</span>
                              <span class="warrant-value">{{ warrantData.issueDate || '-' }}</span>
                            </div>
                          </div>
                          <div class="col-sm-6">
                            <div class="warrant-field">
                              <span class="warrant-label">Reference Number</span>
                              <span class="warrant-value">{{ warrantData.refNumber || '-' }}</span>
                            </div>
                          </div>
                          <div class="col-sm-6">
                            <div class="warrant-field">
                              <span class="warrant-label">MDA</span>
                              <span class="warrant-value">{{ warrantData.mdaName || '-' }}</span>
                            </div>
                          </div>
                          <div class="col-sm-6">
                            <div class="warrant-field">
                              <span class="warrant-label">Org Code</span>
                              <span class="warrant-value">{{ warrantData.orgCode || '-' }}</span>
                            </div>
                          </div>
                          <div class="col-sm-6">
                            <div class="warrant-field">
                              <span class="warrant-label">Amount Released (₦)</span>
                              <span class="warrant-value">{{ formatCurrency(warrantData.amountReleased || 0) }}</span>
                            </div>
                          </div>
                          <div class="col-sm-6">
                            <div class="warrant-field">
                              <span class="warrant-label">Nature of Expenditure</span>
                              <span class="warrant-value">{{ warrantData.natureOfExpenditure || '-' }}</span>
                            </div>
                          </div>
                          <div class="col-sm-6">
                            <div class="warrant-field">
                              <span class="warrant-label">Budget Year</span>
                              <span class="warrant-value">{{ warrantData.budgetYear || '-' }}</span>
                            </div>
                          </div>
                          <div v-if="warrantData.details" class="col-12">
                            <div class="warrant-field">
                              <span class="warrant-label">Details</span>
                              <span class="warrant-value">{{ warrantData.details }}</span>
                            </div>
                          </div>
                          <div v-if="warrantLineItems.length" class="col-12">
                            <div class="warrant-field">
                              <span class="warrant-label">Economic Line Items (ELI)</span>
                              <div class="table-responsive mt-2">
                                <table class="table table-sm table-bordered align-middle mb-0">
                                  <thead class="table-light">
                                    <tr>
                                      <th class="text-center">Eco Code</th>
                                      <th>Title</th>
                                      <th class="text-end">Amount Approved (₦)</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(item, index) in warrantLineItems" :key="`warrant-eli-${item.id || item.mdaLineItemBudgetId || index}`">
                                      <td class="text-center">{{ item.ecoCode || item.economicCode || '-' }}</td>
                                      <td>{{ item.title || '-' }}</td>
                                      <td class="text-end">{{ formatAmount(item.amountApproved || item.amountRecommended || item.eliAmountRecommended || item.amountRequired || item.amtRequired || 0) }}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-if="hasApprovalDocument && !(isGroupHeadOrMember && isApprovedRecord)" class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-start pt-2"><label class="form-label">Approval Document</label></div>
                      <div class="col-md-9 input-right">
                        <div v-if="loadingDocument" class="text-muted small">Loading document...</div>
                        <div v-else-if="approvalDocumentUrl" class="approval-document-wrapper">
                          <div v-if="isImageDocument" class="approval-document-preview mb-3">
                            <img :src="approvalDocumentUrl" alt="Approval document preview" class="approval-document-image" />
                          </div>
                          <a
                            :href="approvalDocumentUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="btn btn-outline-primary btn-sm"
                          >
                            View Document
                          </a>
                        </div>
                        <div v-else class="text-muted small">Document unavailable.</div>
                      </div>
                    </div>




                  </div>
                </div>

                <div class="mt-4">
                  <div v-if="step === 1" class="d-flex justify-content-end">
                    <button type="button" class="btn btn-primary next-btn d-inline-flex align-items-center" @click="handleNext">
                      <span class="me-2">Next</span>
                      <span class="iconify" data-icon="formkit:arrowright" style="font-size: 1.2em;"></span>
                    </button>
                  </div>

                  <div v-else-if="step < totalSteps" class="d-flex justify-content-between">
                    <button type="button" class="btn btn-secondary back-btn" @click="handleBack">
                      <span class="iconify" data-icon="formkit:arrowleft" style="font-size: 1.2em;"></span>
                      Previous
                    </button>
                    <button type="button" class="btn btn-primary next-btn d-inline-flex align-items-center" @click="handleNext">
                      <span class="me-2">Next</span>
                      <span class="iconify" data-icon="formkit:arrowright" style="font-size: 1.2em;"></span>
                    </button>
                  </div>

                  <div v-else class="d-flex justify-content-between">
                    <button type="button" class="btn btn-secondary back-btn" @click="handleBack">
                      <span class="iconify" data-icon="formkit:arrowleft" style="font-size: 1.2em;"></span>
                      Previous
                    </button>

                    <div class="d-flex gap-2">
                      <router-link :to="{ name: 'ViewSpecialApprovals' }" class="btn btn-outline-secondary">Back to list</router-link>
                      <button v-if="canEditDecision" type="button" class="btn btn-warning" @click="handleReturn" :disabled="savingReturn || savingApprove">
                        <span v-if="savingReturn" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {{ savingReturn ? 'Processing...' : 'Return' }}
                      </button>
                      <button v-if="canEditDecision" type="submit" class="btn btn-success" :disabled="savingApprove || savingReturn">
                        <span v-if="savingApprove" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {{ savingApprove ? 'Processing...' : 'Approve' }}
                      </button>
                      <button v-if="canEditReturned" type="submit" class="btn btn-primary" :disabled="savingResubmit">
                        <span v-if="savingResubmit" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {{ savingResubmit ? 'Saving...' : 'Save & Resubmit' }}
                      </button>
                      <button
                        v-if="canGenerateWarrant && !warrantData"
                        type="button"
                        class="btn btn-outline-primary"
                        @click="generateWarrant"
                        :disabled="loadingGenerateWarrant"
                        :aria-label="loadingGenerateWarrant ? 'Generating warrant...' : 'Generate Warrant'"
                      >
                        <span v-if="loadingGenerateWarrant" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {{ loadingGenerateWarrant ? 'Generating...' : 'Generate Warrant' }}
                      </button>
                      <button
                        v-if="warrantData"
                        type="button"
                        class="btn btn-primary"
                        @click="downloadWarrant(warrantData.id)"
                        :disabled="loadingDownloadWarrant"
                      >
                        <span v-if="loadingDownloadWarrant" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {{ loadingDownloadWarrant ? 'Downloading...' : 'Download Warrant' }}
                      </button>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </form>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import Swal from 'sweetalert2'
import { useRoute } from 'vue-router'
import { useUserStore } from '@/stores/user'
import DashboardLayout from '../layout/DashboardLayout.vue'

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const route = useRoute()
const userStore = useUserStore()

const totalSteps = 3
const step = ref(1)
const loading = ref(true)
const savingApprove = ref(false)
const savingReturn = ref(false)
const savingResubmit = ref(false)
const loadingGenerateWarrant = ref(false)
const loadingDownloadWarrant = ref(false)
const loadingDocument = ref(false)
const approvalDocumentUrl = ref('')
const warrantData = ref(null)
const ecoOptions = ref([])
// const searchTerm = ref('')
// const newEcoId = ref(null)
// const selectedEco = ref(null)

const steps = [
  { label: 'General Information' },
  { label: 'Economic Line Item' },
  { label: 'Approval Decision' },
]

const progressWidth = computed(() => {
  const percentPerStep = 100 / steps.length
  return `${step.value * percentPerStep}%`
})

const form = reactive({
  id: '',
  originatingMda: '',
  mdaId: '',
  orgCode: '',
  economicCode: '',
  natOfExpenditure: '',
  approvedEstimates: 0,
  actualExpenditureYTD: 0,
  balance: 0,
  performance: 0,
  amtRequired: 0,
  amtApproved: 0,
  ticketId: null,
  groupTypeId: null,
  initiatorId: null,
  initiatorName: '',
  sectoralHeadName: '',
  dobName: '',
  permSecId: null,
  permSecName: '',
  commissionerId: null,
  commissionerName: '',
  fileApprovedDate: '',
  documentContentType: '',
  returnedInd: 0,
  returnReason: '',
  approvedInd: 0,
  fileNumber: '',
  quarter: 0,
  numberOfWarrants: 0,
  approvedForWarrantInd: 0,
  permSecApprovedInd: 0,
  commissionerApprovedInd: 0,
  purposeOfRequest: '',
  budgetYear: '',
  lineItems: [],
  approvedByPermSec: false,
  approvedByCBP: false,
  approved: false,
  returned: false,
})

const isCommissioner = computed(() => !!userStore.profile?.role?.commOfBAndP)
const isPermSec = computed(() => !!userStore.profile?.role?.perSec)
const isGroupHeadOrMember = computed(() => !!userStore.profile?.role?.groupHead || !!userStore.profile?.role?.groupMember)
const canParticipate = computed(() => isCommissioner.value || isPermSec.value)
const isReturnedRecord = computed(() => Boolean(form.returnedInd || form.returned))

const hasRoleWorked = computed(() =>
  Boolean(
    form.approvedByPermSec ||
    form.approvedByCBP ||
    form.permSecApprovedInd ||
    form.commissionerApprovedInd ||
    form.approvedInd ||
    form.returnedInd ||
    form.approved ||
    form.returned
  )
)

const canEditDecision = computed(() => canParticipate.value && !hasRoleWorked.value)
const canEditReturned = computed(() => isReturnedRecord.value && isGroupHeadOrMember.value)
const isApprovedRecord = computed(() =>
  Boolean(
    form.approvedInd ||
    form.approved ||
    form.approvedByCBP ||
    form.permSecApprovedInd ||
    form.approvedByPermSec
  )
)
const canGenerateWarrant = computed(() => isGroupHeadOrMember.value && isApprovedRecord.value)
const hasApprovalDocument = computed(() => Boolean(form.documentContentType))
const isImageDocument = computed(() => String(form.documentContentType || '').startsWith('image/'))

// const resolvedGroupTypeId = computed(() => userStore.profile?.groupTypeId ?? null)

const totalAmountRequired = computed(() =>
  (form.lineItems || []).reduce((sum, item) => sum + Number(item.amountRequired || item.amtRequired || 0), 0)
)

const totalAmountRecommended = computed(() =>
  (form.lineItems || []).reduce((sum, item) => sum + Number(item.amountRecommended || item.eliAmountRecommended || item.amountApproved || 0), 0)
)

const totalAmountApproved = computed(() =>
  (form.lineItems || []).reduce((sum, item) => sum + Number(item.amountApproved || item.amountRecommended || item.eliAmountRecommended || 0), 0)
)

const warrantLineItems = computed(() => {
  if (Array.isArray(warrantData.value?.lineItems) && warrantData.value.lineItems.length) {
    return warrantData.value.lineItems
  }

  return Array.isArray(form.lineItems) ? form.lineItems : []
})

// const filteredEcoOptions = computed(() =>
//   ecoOptions.value.filter((item) => {
//     const itemId = item.id || item.mdaLineItemBudgetId
//     const exists = (form.lineItems || []).some((lineItem) => {
//       const lineItemId = lineItem.id || lineItem.mdaLineItemBudgetId
//       return Number(lineItemId) === Number(itemId)
//     })

//     if (exists) return false

//     return [item.ecoCode, item.title]
//       .join(' ')
//       .toLowerCase()
//       .includes(searchTerm.value.toLowerCase())
//   })
// )

const statusLabel = computed(() => {
  if (form.returnedInd || form.returned) return 'Returned'
  if (form.approvedInd || form.approved || form.approvedByCBP) return 'Approved'
  if (form.approvedByPermSec || form.permSecApprovedInd) return 'Perm Sec Approved'
  return 'Pending'
})

const natureLabel = computed(() => {
  if (String(form.natOfExpenditure) === '3') return 'Capital'
  if (String(form.natOfExpenditure) === '2') return 'Recurrent'
  if (String(form.natOfExpenditure) === '4') return 'Hybrid'
  return form.natOfExpenditure || '-'
})

const formatAmount = (value) => {
  if (value === '' || value === null) return ''
  const num = parseFloat(value).toFixed(2)
  return Number(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const formatCurrency = (amount) => `₦${formatAmount(amount)}`

const MAX_SAFE_RAW = Number.MAX_SAFE_INTEGER.toString()

const onItemAmountInput = (event, item) => {
  if (!event?.target) return
  let raw = event.target.value.replace(/\D/g, '')

  if (raw === '') {
    item.amountRequired = ''
    event.target.value = ''
    return
  }

  if (raw.length > MAX_SAFE_RAW.length || (raw.length === MAX_SAFE_RAW.length && raw > MAX_SAFE_RAW)) {
    raw = MAX_SAFE_RAW
  }

  const num = parseInt(raw, 10) / 100
  item.amountRequired = Number(num.toFixed(2))
  event.target.value = formatAmount(item.amountRequired)
}

const onItemApprovedInput = (event, item) => {
  if (!event?.target) return
  let raw = event.target.value.replace(/\D/g, '')

  if (raw === '') {
    item.amountApproved = ''
    event.target.value = ''
    return
  }

  if (raw.length > MAX_SAFE_RAW.length || (raw.length === MAX_SAFE_RAW.length && raw > MAX_SAFE_RAW)) {
    raw = MAX_SAFE_RAW
  }

  const num = parseInt(raw, 10) / 100
  item.amountApproved = Number(num.toFixed(2))
  event.target.value = formatAmount(item.amountApproved)
}

const toDateTimeLocal = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

const fromDateTimeLocalToIso = (value) => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

const normalizeNumericFlags = () => {
  form.returnedInd = Number(form.returnedInd || 0)
  form.approvedInd = Number(form.approvedInd || 0)
  form.permSecApprovedInd = Number(form.permSecApprovedInd || 0)
  form.commissionerApprovedInd = Number(form.commissionerApprovedInd || 0)
}

const getNatureCodeForApi = (value) => {
  if (String(value) === '2' || String(value) === '3') return String(value)
  const normalized = String(value || '').toLowerCase()
  if (normalized === 'recurrent') return '2'
  if (normalized === 'capital') return '3'
  return ''
}

const revokeApprovalDocumentUrl = () => {
  if (approvalDocumentUrl.value) {
    window.URL.revokeObjectURL(approvalDocumentUrl.value)
    approvalDocumentUrl.value = ''
  }
}

const fetchApprovalDocument = async () => {
  const id = route.params.id

  if (!id || !hasApprovalDocument.value) {
    revokeApprovalDocumentUrl()
    return
  }

  const token = Cookies.get('auth_token')
  loadingDocument.value = true

  try {
    const response = await axios.get(`${root_api_url}/appraisal/special-approval/${id}/document`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    })

    revokeApprovalDocumentUrl()
    approvalDocumentUrl.value = window.URL.createObjectURL(response.data)
  } catch (error) {
    revokeApprovalDocumentUrl()
    console.error('Failed to load approval document:', error)
  } finally {
    loadingDocument.value = false
  }
}

const fetchEcoOptions = async () => {
  const natureCode = getNatureCodeForApi(form.natOfExpenditure)

  if (!form.mdaId || !natureCode) {
    ecoOptions.value = []
    return
  }

  const token = Cookies.get('auth_token')
  const year = form.budgetYear || new Date().getFullYear()

  try {
    const { data } = await axios.get(
      `${root_api_url}/appraisal/appraisal/budget/${form.mdaId}/${year}/${natureCode}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )

    ecoOptions.value = (data.ecoLineItemDtos || []).map((dto) => ({
      id: dto.id,
      mdaLineItemBudgetId: dto.id,
      ecoCode: dto.economicCode,
      title: dto.title,
      amountBudgeted: Number(dto.amountBudgeted || 0),
      amountReleased: Number(dto.amountReleased || 0),
      balance: Number(dto.balance || 0),
    }))
  } catch {
    ecoOptions.value = []
  }
}

// const onSelectEco = () => {
//   selectedEco.value = ecoOptions.value.find((item) => Number(item.id) === Number(newEcoId.value)) || null
// }

// const addSelectedEco = () => {
//   const selected = selectedEco.value || ecoOptions.value.find((item) => Number(item.id) === Number(newEcoId.value)) || null
//   if (!selected) return

//   const itemId = selected.id || selected.mdaLineItemBudgetId
//   const exists = (form.lineItems || []).some((item) => {
//     const lineItemId = item.id || item.mdaLineItemBudgetId
//     return Number(lineItemId) === Number(itemId)
//   })

//   if (exists) {
//     Swal.fire({ icon: 'warning', text: 'This economic item is already added.' })
//     return
//   }

//   form.lineItems.push({
//     id: selected.id,
//     mdaLineItemBudgetId: selected.mdaLineItemBudgetId,
//     ecoCode: selected.ecoCode,
//     title: selected.title,
//     amountBudgeted: Number(selected.amountBudgeted || 0),
//     amountReleased: Number(selected.amountReleased || 0),
//     balance: Number(selected.balance || 0),
//     amountRequired: 0,
//     amountRecommended: 0,
//     amountApproved: 0,
//   })

//   newEcoId.value = null
//   selectedEco.value = null
//   searchTerm.value = ''
// }

// const removeLineItem = (index) => {
//   if (index < 0 || index >= form.lineItems.length) return
//   form.lineItems.splice(index, 1)
// }

const fetchSpecialApproval = async () => {
  const token = Cookies.get('auth_token')
  const id = route.params.id

  loading.value = true
  warrantData.value = null
  try {
    const { data } = await axios.get(`${root_api_url}/appraisal/special-approval/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    Object.assign(form, {
      ...form,
      ...data,
      fileApprovedDate: toDateTimeLocal(data.fileApprovedDate),
      lineItems: Array.isArray(data.lineItems) ? data.lineItems : [],
      returnReason: data.returnReason || '',
      purposeOfRequest: data.purposeOfRequest || '',
    })

    normalizeNumericFlags()
    setInitialStep()

    if (hasApprovalDocument.value) {
      await fetchApprovalDocument()
    } else {
      revokeApprovalDocumentUrl()
    }

    if (canEditReturned.value) {
      await fetchEcoOptions()
    }
  } catch (error) {
    const message = error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to load special appraisal.'
    Swal.fire({ icon: 'error', text: message })
  } finally {
    loading.value = false
  }
}

const handleNext = () => {
  if (step.value < totalSteps) step.value += 1
}

const handleBack = () => {
  if (step.value > 1) step.value -= 1
}

watch(
  [canEditReturned, () => form.mdaId, () => form.budgetYear, () => form.natOfExpenditure],
  async ([editable]) => {
    if (!editable) return
    await fetchEcoOptions()
  }
)

const buildPayload = (overrides = {}) => ({
  ...form,
  amtRequired: Number(totalAmountRequired.value || 0),
  amtApproved: Number(totalAmountApproved.value || 0),
  fileApprovedDate: fromDateTimeLocalToIso(form.fileApprovedDate),
  returnedInd: Number(form.returnedInd || 0),
  approvedInd: Number(form.approvedInd || 0),
  permSecApprovedInd: Number(form.permSecApprovedInd || 0),
  commissionerApprovedInd: Number(form.commissionerApprovedInd || 0),
  lineItems: (form.lineItems || []).map((item) => ({
    ...item,
    amountRequired: Number(item.amountRequired || 0),
    amountRecommended: Number(item.amountRecommended || item.eliAmountRecommended || 0),
    amountApproved: Number(item.amountApproved || item.amountRecommended || item.eliAmountRecommended || 0),
    amountBudgeted: Number(item.amountBudgeted || 0),
    amountReleased: Number(item.amountReleased || 0),
    balance: Number(item.balance || 0),
  })),
  ...overrides,
})

const setInitialStep = () => {
  if (isApprovedRecord.value || canEditDecision.value) {
    step.value = totalSteps
    return
  }

  step.value = 1
}

const handlePrimarySubmit = async () => {
  if (canEditReturned.value) {
    await handleResubmit()
    return
  }

  await handleApprove()
}

const handleApprove = async () => {
  if (!canEditDecision.value) return

  if (!form.fileApprovedDate) {
    Swal.fire({ icon: 'warning', text: 'Please select file approved date.' })
    return
  }

  const token = Cookies.get('auth_token')
  const id = route.params.id

  savingApprove.value = true
  try {
    const roleFlags = isCommissioner.value
      ? { commissionerApprovedInd: 1, approvedInd: 1, returnedInd: 0 }
      : { permSecApprovedInd: 1, approvedInd: 1, returnedInd: 0 }

    const payload = buildPayload(roleFlags)

    await axios.put(`${root_api_url}/appraisal/special-approval/${id}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    await Swal.fire({ icon: 'success', text: 'Special appraisal approved successfully.' })
    await fetchSpecialApproval()
  } catch (error) {
    const message = error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to approve special appraisal.'
    Swal.fire({ icon: 'error', text: message })
  } finally {
    savingApprove.value = false
  }
}

const handleReturn = async () => {
  if (!canEditDecision.value) return

  const { value: reason } = await Swal.fire({
    title: 'Return Special Appraisal',
    input: 'textarea',
    inputLabel: 'Reason for return',
    inputValue: form.returnReason || '',
    inputPlaceholder: 'Type return reason here...',
    showCancelButton: true,
    confirmButtonText: 'Return',
    cancelButtonText: 'Cancel',
    preConfirm: (value) => {
      if (!value || !String(value).trim()) {
        Swal.showValidationMessage('Return reason is required.')
      }
      return String(value || '').trim()
    },
  })

  if (!reason) return

  form.returnReason = reason

  const token = Cookies.get('auth_token')
  const id = route.params.id

  savingReturn.value = true
  try {
    const payload = buildPayload({
      returnReason: form.returnReason,
      returnedInd: 1,
      approvedInd: 0,
      permSecApprovedInd: 0,
      commissionerApprovedInd: 0,
    })

    await axios.put(`${root_api_url}/appraisal/special-approval/${id}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    await Swal.fire({ icon: 'success', text: 'Special appraisal returned successfully.' })
    await fetchSpecialApproval()
  } catch (error) {
    const message = error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to return special appraisal.'
    Swal.fire({ icon: 'error', text: message })
  } finally {
    savingReturn.value = false
  }
}

const handleResubmit = async () => {
  if (!canEditReturned.value) return

  const token = Cookies.get('auth_token')
  const id = route.params.id

  savingResubmit.value = true
  try {
    const payload = buildPayload({
      returnedInd: 0,
      approvedInd: 0,
      permSecApprovedInd: 0,
      commissionerApprovedInd: 0,
      approvedByPermSec: false,
      approvedByCBP: false,
      approved: false,
      returned: false,
      returnReason: null,
    })

    await axios.put(`${root_api_url}/appraisal/special-approval/${id}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    await Swal.fire({ icon: 'success', text: 'Special appraisal updated and resubmitted successfully.' })
    await fetchSpecialApproval()
  } catch (error) {
    const message = error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to resubmit special appraisal.'
    Swal.fire({ icon: 'error', text: message })
  } finally {
    savingResubmit.value = false
  }
}

const generateWarrant = async () => {
  const token = Cookies.get('auth_token')
  const id = route.params.id

  if (!id) return

  loadingGenerateWarrant.value = true
  try {
    const { data } = await axios.post(
      `${root_api_url}/appraisal/special-approval/warrant/${id}`,
      buildPayload(),
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    )

    if (data?.errorMessage) {
      Swal.fire({ icon: 'error', text: data.errorMessage })
      return
    }

    warrantData.value = data
  } catch (error) {
    const message = error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to generate warrant.'
    Swal.fire({ icon: 'error', text: message })
  } finally {
    loadingGenerateWarrant.value = false
  }
}

const downloadWarrant = async (warrantId) => {
  const token = Cookies.get('auth_token')

  if (!warrantId) return

  loadingDownloadWarrant.value = true
  try {
    const response = await axios.get(`${root_api_url}/appraisal/warrant/pdf/2/${warrantId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    })

    let downloadName = 'warrant.pdf'
    const disposition = response.headers['content-disposition']

    if (disposition && disposition.includes('filename=')) {
      downloadName = disposition
        .split('filename=')[1]
        .replace(/"/g, '')
    }

    const blob = new Blob([response.data], { type: response.data.type })

    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, downloadName)
    } else {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', downloadName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
  } catch (error) {
    const message = error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to download warrant.'
    Swal.fire({ icon: 'error', text: message })
  } finally {
    loadingDownloadWarrant.value = false
  }
}

onMounted(async () => {
  document.title = 'Approve Special Appraisal'

  if (!userStore.profile) {
    await userStore.fetchProfile()
  }

  await fetchSpecialApproval()
})

onBeforeUnmount(() => {
  revokeApprovalDocumentUrl()
})
</script>

<style scoped>
.view-only-input {
  border: none !important;
  background-color: transparent;
  padding-left: 0;
  cursor: default;
  pointer-events: none;
  color: #212529;
  font-size: 1rem;
  padding-right: 0;
  margin-right: 1rem;
  margin-bottom: 0.5rem !important;
}

.budget-item-wrapper .content-wrapper {
  margin: 0;
}

.budget-item-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
}

.budget-item-wrapper {
  margin-top: 20px;
  padding: 0 40px;
}

.content-wrapper {
  border: 1px solid var(--border);
  background-color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  padding: 1rem;
}

.section-border {
  border-top: 3px solid #667eea !important;
}

.step-2 {
  padding: 0 20px;
}

.budget-item-wrapper.step-2 .col {
  border-right: 1px solid var(--border);
}

.budget-item-wrapper.step-2 .col.no-border {
  border-right: none !important;
}

.budget-item-wrapper.step-2 .fixed-label {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
}

.budget-item-wrapper.step-2 .view-only-input {
  text-align: center !important;
}

.approval-document-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.warrant-details-card {
  border: 1px solid #dee2e6;
  border-top: 3px solid #667eea;
  border-radius: 8px;
  padding: 1.25rem 1.5rem;
  background-color: #fff;
  margin-bottom: 1.5rem;
}

.warrant-details-header {
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.75rem;
}

.warrant-field {
  display: flex;
  flex-direction: column;
}

.warrant-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.2rem;
}

.warrant-value {
  font-size: 0.95rem;
  color: #212529;
  font-weight: 500;
}

.approval-document-preview {
  width: 100%;
  max-width: 520px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0.75rem;
  background-color: #fff;
}

.approval-document-image {
  display: block;
  width: 100%;
  max-height: 420px;
  object-fit: contain;
  border-radius: 6px;
}
</style>
